/**
 * Stage 32 · Knowledge & SOP Intelligence.
 * Approved-source-only answers. No hallucinated SOPs.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Search, Plus, MessageCircle, AlertTriangle, ThumbsUp, ThumbsDown, ListChecks } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-500 border-slate-200",
  review: "bg-sky-100 text-sky-800 border-sky-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  superseded: "bg-amber-100 text-amber-800 border-amber-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Knowledge() {
  const [tab, setTab] = useState("articles");
  return (
    <div className="max-w-6xl space-y-5" data-testid="knowledge-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Knowledge · SOP Intelligence</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">ONE Knowledge</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Approved SOPs, work instructions, policies, procedures and FAQs — versioned, role-aware and context-aware. If no approved instruction exists for a situation, ONE says so instead of inventing one.
        </p>
        <div className="flex items-center gap-2">
          <Link to="/sop" className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white inline-flex items-center gap-1.5" data-testid="kb-to-sop"><ListChecks className="h-4 w-4" /> Step-by-step SOP Centre</Link>
        </div>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { key: "articles",   label: "Articles",       icon: BookOpen },
          { key: "ask",        label: "Ask Knowledge",  icon: MessageCircle },
          { key: "context",    label: "Contextual",     icon: Search },
          { key: "gaps",       label: "Knowledge gaps", icon: AlertTriangle },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`kb-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "articles" && <ArticlesTab />}
      {tab === "ask"      && <AskTab />}
      {tab === "context"  && <ContextTab />}
      {tab === "gaps"     && <GapsTab />}
    </div>
  );
}

function StatusChip({ v }) {
  return <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (STATUS_STYLES[v] || STATUS_STYLES.draft)}>{v}</span>;
}

/* ---- Articles ---- */
function ArticlesTab() {
  const [items, setItems] = useState([]);
  const [enums, setEnums] = useState({ categories: [], statuses: [], visibilities: [] });
  const [q, setQ] = useState(""); const [cat, setCat] = useState(""); const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => api.get("/knowledge/articles", { params: { q: q || undefined, category: cat || undefined, status: status || undefined } })
    .then(({ data }) => { setItems(data.items || []); setEnums({ categories: data.categories || [], statuses: data.statuses || [], visibilities: data.visibilities || [] }); }), [q, cat, status]);
  useEffect(() => { load(); }, [load]);
  const transition = async (id, to) => {
    try { await api.post(`/knowledge/articles/${id}/transition`, { to_status: to }); toast.success(`→ ${to}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="kb-articles">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[200px]"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Search</div><input className="input w-full" value={q} onChange={(e) => setQ(e.target.value)} placeholder="title, tag…" data-testid="kb-search" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</div><select className="input" value={cat} onChange={(e) => setCat(e.target.value)}><option value="">All</option>{enums.categories.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</div><select className="input" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All</option>{enums.statuses.map((c) => <option key={c}>{c}</option>)}</select></label>
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="kb-new"><Plus className="h-4 w-4" /> New article</button>
      </div>
      {showForm && <ArticleForm enums={enums} onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Title</th><th className="text-left py-2 px-3">Category</th><th className="text-left py-2 px-3">v</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Visibility</th><th className="text-right py-2 px-3"></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No articles.</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`kb-row-${a.id}`}>
                <td className="py-2 px-3 text-slate-900 cursor-pointer" onClick={() => setSelected(a)}>{a.title}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{a.category}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">v{a.version || 1}</td>
                <td className="py-2 px-3"><StatusChip v={a.status} /></td>
                <td className="py-2 px-3 text-slate-600 text-xs">{a.visibility}</td>
                <td className="py-2 px-3 text-right">
                  {a.status === "draft" && <button onClick={() => transition(a.id, "review")} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1">→ Review</button>}
                  {a.status === "review" && <button onClick={() => transition(a.id, "approved")} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1">Approve</button>}
                  {a.status === "approved" && <button onClick={() => transition(a.id, "published")} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`kb-publish-${a.id}`}>Publish</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <ArticleDrawer article={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ArticleForm({ enums, onCancel, onSaved }) {
  const [f, setF] = useState({ title: "", category: "sop", description: "", content: "", customer: "", country: "", service: "", role: "", visibility: "internal", tags: "" });
  const save = async () => {
    try { await api.post("/knowledge/articles", { ...f, tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) });
          toast.success("Draft saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="kb-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div><input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="kb-title" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</div><select className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{enums.categories.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Customer scope (mc id)</div><input className="input" value={f.customer} onChange={(e) => setF({ ...f, customer: e.target.value })} placeholder="leave blank for global" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Country</div><input className="input" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="e.g. IN" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Service</div><input className="input" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Visibility</div><select className="input" value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })}>{enums.visibilities.map((v) => <option key={v}>{v}</option>)}</select></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Tags (comma-separated)</div><input className="input" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="recovery, macbook, chennai" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Content</div><textarea className="input min-h-[160px] font-mono text-xs" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="kb-content" /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
        <button onClick={save} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="kb-save">Save draft</button>
      </div>
    </div>
  );
}

function ArticleDrawer({ article, onClose }) {
  const feedback = async (kind) => {
    const comment = kind === "helpful" ? null : window.prompt("Comment (optional)?") || null;
    try { await api.post("/knowledge/feedback", { knowledge_id: article.id, kind, comment }); toast.success("Thanks — feedback recorded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ack = async () => {
    try { await api.post("/knowledge/acknowledge", { knowledge_id: article.id, read: true, training_completed: false }); toast.success("Acknowledged"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex justify-end" onClick={onClose} data-testid="kb-drawer">
      <div className="w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{article.category} · v{article.version || 1} · {article.status}</div>
          <h2 className="text-xl font-heading font-semibold text-slate-900 tracking-tight mt-1">{article.title}</h2>
          {article.description && <div className="text-sm text-slate-600 mt-1">{article.description}</div>}
        </div>
        <div className="p-5 whitespace-pre-wrap text-sm text-slate-800 leading-6">{article.content}</div>
        <div className="p-5 border-t border-slate-200 flex flex-wrap gap-2">
          <button onClick={() => feedback("helpful")}          className="h-9 px-3 text-sm rounded-md border border-emerald-200 text-emerald-800 bg-emerald-50 inline-flex items-center gap-1" data-testid="kb-fb-helpful"><ThumbsUp className="h-4 w-4" /> Helpful</button>
          <button onClick={() => feedback("outdated")}         className="h-9 px-3 text-sm rounded-md border border-amber-200 text-amber-800 bg-amber-50 inline-flex items-center gap-1" data-testid="kb-fb-outdated"><ThumbsDown className="h-4 w-4" /> Outdated</button>
          <button onClick={() => feedback("missing_information")} className="h-9 px-3 text-sm rounded-md border border-amber-200 text-amber-800 bg-amber-50 inline-flex items-center gap-1">Missing info</button>
          <button onClick={ack}                                className="ml-auto h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="kb-ack">I have read this</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Ask ---- */
function AskTab() {
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/knowledge/ask", { question: q }); setRes(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="kb-ask">
      <div className="flex items-start gap-2">
        <textarea className="input flex-1 min-h-[70px]" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question…" data-testid="kb-ask-input" />
        <button onClick={ask} disabled={busy} className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60" data-testid="kb-ask-submit">Ask</button>
      </div>
      {res && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="kb-ask-answer">
          {res.answered ? (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Answer · from approved knowledge</div>
              <div className="text-sm font-medium text-slate-900 mt-1">{res.title}</div>
              <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{res.excerpt}…</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">
                Source · {res.source.category} v{res.source.version} · Effective {res.source.effective_from?.slice(0, 10) || "—"}
                {res.source.customer ? ` · customer ${res.source.customer.slice(0, 8)}` : ""}
                {res.source.country ? ` · country ${res.source.country}` : ""}
              </div>
            </>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3" data-testid="kb-ask-no-answer">
              <div className="font-medium">{res.reason}</div>
              <div className="mt-1">{res.recommendation}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---- Contextual ---- */
function ContextTab() {
  const [f, setF] = useState({ customer: "", country: "", service: "", role: "", category: "" });
  const [items, setItems] = useState([]); const [used, setUsed] = useState([]);
  const lookup = async () => {
    try { const { data } = await api.get("/knowledge/context", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) });
          setItems(data.items || []); setUsed(data.priority_used || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="kb-context">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {["customer","country","service","role","category"].map((k) => (
          <label key={k}><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{k}</div><input className="input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
        ))}
      </div>
      <button onClick={lookup} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="kb-ctx-lookup">Look up</button>
      {items.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Matched at priority · {JSON.stringify(used[0] || {})}</div>
          <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-md">
            {items.map((a) => (
              <li key={a.id} className="p-3 text-sm"><div className="font-medium text-slate-900">{a.title}</div><div className="text-slate-600 text-xs">{a.category} · v{a.version} · {a.status}</div></li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* ---- Gaps ---- */
function GapsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/knowledge/gaps").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="kb-gaps">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Trigger</th><th className="text-left py-2 px-3">Knowledge</th><th className="text-left py-2 px-3">Comment</th><th className="text-left py-2 px-3">Detected</th></tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={4} className="py-6 text-center text-slate-500">No open knowledge gaps.</td></tr>
            : items.map((g) => (
              <tr key={g.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-700">{g.trigger}</td>
                <td className="py-2 px-3 text-slate-700 text-xs">{g.knowledge_id?.slice(0, 8)}</td>
                <td className="py-2 px-3 text-slate-800">{g.comment || "—"}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{g.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
